import type { Ref } from 'vue'
import { getMainRole } from '~/utils/other'

// must stay below the server-side TOKEN_REFRESH_WINDOW (60s) so a session
// returned by /api/auth/session is never immediately considered stale again
const STALE_MARGIN_MS = 30_000

// After this much time past accessTokenExpired with refresh still failing the
// session is treated as dead. Transient refresh failures keep the old token
// alive server-side (no error flag) so every cycle retries until then; signing
// out earlier would just redirect to a possibly-unreachable Keycloak.
const DEAD_SESSION_GRACE_MS = 2 * 60_000

type AuthSession = {
  accessToken?: string
  accessTokenExpired?: number
  error?: string
  user?: {
    roles?: {
      'x-hasura-allowed-roles': string[]
    }
  }
} | null | undefined

export interface AuthTokenHelper {
  session: Ref<AuthSession>
  isTokenStale: (marginMs?: number) => boolean
  hasRefreshError: () => boolean
  getFreshSession: (opts?: { force?: boolean }) => Promise<AuthSession>
  getAccessToken: () => Promise<string | null>
  getRole: () => string
  handleDeadSession: (opts?: { callbackUrl?: string, respectDevMode?: boolean }) => Promise<void>
}

let helperSingleton: AuthTokenHelper | null = null

// Shared by every caller that can detect a dead session (api plugin, one urql client per
// data endpoint, client init, route middleware) so the user is signed out exactly once.
// While set, no further /api/auth/session request is made: a response arriving after
// /signout would write the dead session cookie back and the next page load would end up
// in the same state again (the "dashboard reloads several times" bug).
let signingOut = false

/**
 * Single deduplicated token source on top of nuxt-auth's session state.
 * /api/auth/session is only fetched when there is no session yet, the token
 * is within STALE_MARGIN_MS of expiry, or a caller forces it after a 401.
 * Must be called from a Nuxt context (plugin/composable) at least once;
 * afterwards the singleton is safe to use from interceptors and callbacks.
 */
export const useAuthToken = (): AuthTokenHelper => {
  if (helperSingleton && !process.server) {
    return helperSingleton
  }

  const { data, getSession, signIn, signOut } = useAuth()
  const session = data as Ref<AuthSession>
  let inflight: Promise<AuthSession> | null = null

  // bound once in the Nuxt context: handleDeadSession is also called from interceptors and callbacks
  const nuxtApp = useNuxtApp()
  const router = useRouter()
  const envVars = useRuntimeConfig()

  const isTokenStale = (marginMs: number = STALE_MARGIN_MS): boolean => {
    const s = session.value
    return signingOut || !s?.accessToken || !!s.error || Date.now() > (s.accessTokenExpired ?? 0) - marginMs
  }

  // definitive: the server could not refresh because the refresh token is expired or revoked
  const hasRefreshError = (): boolean => session.value?.error === 'RefreshAccessTokenError'

  const isSessionDead = (): boolean => {
    const s = session.value
    return !s?.accessToken ||
      s.error === 'RefreshAccessTokenError' ||
      Date.now() > (s.accessTokenExpired ?? 0) + DEAD_SESSION_GRACE_MS
  }

  // resolves to null only when the session is genuinely dead
  const getFreshSession = async ({ force = false }: { force?: boolean } = {}): Promise<AuthSession> => {
    if (signingOut) {
      return null
    }

    if (!force && !isTokenStale()) {
      return session.value
    }

    if (!inflight) {
      inflight = getSession()
        .then(() => (isSessionDead() ? null : session.value))
        // transient network failure: keep the current session, a sign-out
        // must only be triggered by a real RefreshAccessTokenError
        .catch(() => (session.value?.accessToken ? session.value : null))
        .finally(() => { inflight = null })
    }

    return await inflight
  }

  const getAccessToken = async (): Promise<string | null> => {
    const s = await getFreshSession()
    return s?.accessToken ?? null
  }

  const getRole = (): string => {
    return getMainRole(session.value?.user?.roles?.['x-hasura-allowed-roles'])
  }

  // Signs a genuinely dead session out and goes straight to the Keycloak login page.
  // The Keycloak end-session call is skipped on purpose: it needs a valid token and
  // always fails with 401 here, which only triggered another forced session refresh.
  const handleDeadSession = async (
    { callbackUrl, respectDevMode = true }: { callbackUrl?: string, respectDevMode?: boolean } = {}
  ): Promise<void> => {
    if (signingOut) {
      return
    }

    if (respectDevMode && envVars.public.ENVIRONMENT === 'dev') {
      nuxtApp.$toast.warn('User would be signed out in non dev env')
      return
    }

    signingOut = true
    nuxtApp.$logger.info('Logging out - session expired')
    const target = callbackUrl ?? router.currentRoute.value.fullPath

    try {
      await nuxtApp.$sentry.flush()
    } catch (e) {
      nuxtApp.$logger.error(e)
    }

    try {
      // clears the session cookie without the reload of the current page a redirecting signOut does
      await signOut({ redirect: false })
    } catch (e) {
      nuxtApp.$logger.error(e)
    }

    try {
      await signIn('keycloak', { callbackUrl: target })
    } catch (e) {
      // keep the app usable instead of blocking every further request forever
      nuxtApp.$logger.error(e)
      signingOut = false
    }
  }

  const helper: AuthTokenHelper = { session, isTokenStale, hasRefreshError, getFreshSession, getAccessToken, getRole, handleDeadSession }

  if (!process.server) {
    helperSingleton = helper
  }

  return helper
}
